/**
 * StudyPlanProjectionChart
 *
 * Renders a compact, teen-friendly "where your score is headed" graph for a
 * student's study plan:
 * - Concave (diminishing-returns) projected COMBINED score trajectory (primary)
 * - Math and R&W section trajectories (secondary)
 * - A shaded confidence band around the combined projection
 * - A goal line, a clear "Today" marker, and milestone markers
 * - An OPTIONAL expected level/mastery track on a secondary axis, hidden by
 *   default behind a toggle to reduce clutter
 *
 * Built on Recharts, mirroring patterns from CombinedTrendChart.
 */

import { useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import {
  ComposedChart,
  Line,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  ReferenceDot,
  ResponsiveContainer
} from 'recharts'
import { TrendingUp, Flag, ChevronDown, ChevronUp } from 'lucide-react'
import useChartTheme from '../../hooks/useChartTheme'

const COMBINED_COLOR = '#6366f1' // indigo
const MATH_COLOR = '#3b82f6' // blue
const RW_COLOR = '#10b981' // green
const GOAL_COLOR = '#f59e0b' // amber
const LEVEL_COLOR = '#a855f7' // purple

const formatDate = (ts) =>
  ts ? new Date(ts).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : ''

const ProjectionTooltip = ({ active, payload, theme, showLevel }) => {
  if (!active || !payload?.length) return null
  const row = payload[0]?.payload
  if (!row) return null
  return (
    <div
      className="rounded-lg px-3 py-2 text-xs shadow-lg border"
      style={{
        backgroundColor: theme?.tooltip?.bg,
        borderColor: theme?.tooltip?.border,
        color: theme?.tooltip?.text
      }}
    >
      <p className="opacity-75 mb-1">By {formatDate(row.date)}</p>
      <p className="font-semibold" style={{ color: COMBINED_COLOR }}>
        Projected total: {row.combined}
      </p>
      <p className="opacity-75">
        Likely range {row.low} to {row.high}
      </p>
      <p style={{ color: MATH_COLOR }}>Math: {row.math}</p>
      <p style={{ color: RW_COLOR }}>Reading and Writing: {row.rw}</p>
      {showLevel && <p style={{ color: LEVEL_COLOR }}>Est. level: {row.level}</p>}
    </div>
  )
}

ProjectionTooltip.propTypes = {
  active: PropTypes.bool,
  payload: PropTypes.array,
  theme: PropTypes.object,
  showLevel: PropTypes.bool
}

const StudyPlanProjectionChart = ({ data, height = 230, className = '' }) => {
  const theme = useChartTheme()
  const [showLevel, setShowLevel] = useState(true)
  const [collapsed, setCollapsed] = useState(false)

  const { rows, milestones, goalScore, hasData } = useMemo(() => {
    if (!data?.weeks?.length) {
      return { rows: [], milestones: [], goalScore: data?.goalScore, hasData: false }
    }
    const rows = data.weeks.map((w) => ({
      date: new Date(w.date).getTime(),
      combined: w.combined,
      math: w.math,
      rw: w.rw,
      level: w.level,
      low: w.low,
      bandRange: Math.max(0, w.high - w.low),
      high: w.high
    }))
    return {
      rows,
      milestones: (data.milestones || []).filter((m) => m.reachedByTestDate && m.date),
      goalScore: data.goalScore,
      hasData: true
    }
  }, [data])

  if (!hasData) {
    return (
      <div
        className={`rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-6 flex flex-col items-center justify-center ${className}`}
        style={{ minHeight: height }}
      >
        <TrendingUp className="text-gray-400 dark:text-gray-500 mb-3" size={32} />
        <p className="text-gray-600 dark:text-gray-300 font-medium">Projection not available yet</p>
        <span className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Set a goal and test date to see your expected trajectory
        </span>
      </div>
    )
  }

  const sectionScores = rows.flatMap((r) => [r.math, r.rw])
  const minScore = Math.max(200, Math.floor((Math.min(...sectionScores) - 40) / 10) * 10)
  const maxScore = Math.min(
    1600,
    Math.ceil((Math.max(goalScore || 0, ...rows.map((r) => r.high)) + 40) / 10) * 10
  )
  const todayTs = rows[0]?.date
  const lastRow = rows[rows.length - 1]
  const testTs = lastRow?.date
  const finalCombined = lastRow?.combined
  const finalLow = lastRow?.low
  const finalHigh = lastRow?.high

  return (
    <div
      className={`rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 ${className}`}
    >
      <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700">
        <div className="flex items-center justify-between gap-2">
          <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-200 flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-indigo-500" />
            Where your score is headed
          </h4>
          <button
            type="button"
            onClick={() => setCollapsed((v) => !v)}
            aria-expanded={!collapsed}
            className="flex items-center gap-1 text-xs font-medium text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
          >
            {collapsed ? 'Show chart' : 'Hide chart'}
            {collapsed ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
          </button>
        </div>
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          If you keep this pace, here is where your score is headed by test day.
        </p>
        {goalScore && (
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <span
              className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium"
              style={{ backgroundColor: `${GOAL_COLOR}22`, color: GOAL_COLOR }}
            >
              <Flag className="w-3 h-3" />
              Goal {goalScore}
              {testTs ? ` by ${formatDate(testTs)}` : ''}
            </span>
          </div>
        )}
      </div>

      {!collapsed && (
        <>
          <div className="flex flex-wrap items-center justify-between gap-2 px-4 pt-3 text-xs text-gray-500 dark:text-gray-400">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <span className="flex items-center gap-1 font-medium text-gray-600 dark:text-gray-300">
                <span className="w-3 h-1 rounded" style={{ backgroundColor: COMBINED_COLOR }} />{' '}
                Total
              </span>
              <span className="flex items-center gap-1">
                <span className="w-3 h-0.5" style={{ backgroundColor: MATH_COLOR }} /> Math
              </span>
              <span className="flex items-center gap-1">
                <span className="w-3 h-0.5" style={{ backgroundColor: RW_COLOR }} /> R&amp;W
              </span>
              <span className="flex items-center gap-1">
                <span
                  className="inline-block w-3 border-t-2 border-dashed"
                  style={{ borderColor: GOAL_COLOR }}
                />{' '}
                Goal
              </span>
              <span className="flex items-center gap-1">
                <span
                  className="inline-block h-2.5 w-3 rounded-sm"
                  style={{ backgroundColor: `${COMBINED_COLOR}33` }}
                />{' '}
                Likely range
              </span>
              {showLevel && (
                <span className="flex items-center gap-1">
                  <span
                    className="inline-block w-3 border-t-2 border-dashed"
                    style={{ borderColor: LEVEL_COLOR }}
                  />{' '}
                  Est. level
                </span>
              )}
            </div>
            <button
              type="button"
              onClick={() => setShowLevel((v) => !v)}
              aria-pressed={showLevel}
              className="rounded-md border border-gray-200 dark:border-gray-600 px-2 py-0.5 font-medium hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
            >
              {showLevel ? 'Hide level track' : 'Show level track'}
            </button>
          </div>

          <div className="px-2 py-4" style={{ height }}>
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart
                data={rows}
                margin={{ top: 8, right: showLevel ? 48 : 12, bottom: 5, left: 8 }}
              >
                <defs>
                  <linearGradient id="sppBand" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={COMBINED_COLOR} stopOpacity={0.18} />
                    <stop offset="100%" stopColor={COMBINED_COLOR} stopOpacity={0.04} />
                  </linearGradient>
                </defs>

                <CartesianGrid strokeDasharray="4 4" stroke={theme.grid} />

                <XAxis
                  dataKey="date"
                  type="number"
                  domain={['dataMin', 'dataMax']}
                  scale="time"
                  tickFormatter={formatDate}
                  tick={{ fill: theme.axisLabel, fontSize: 11 }}
                  tickLine={false}
                  axisLine={{ stroke: theme.grid }}
                />
                <YAxis
                  yAxisId="score"
                  domain={[minScore, maxScore]}
                  tick={{ fill: theme.axisLabel, fontSize: 11 }}
                  tickLine={false}
                  axisLine={false}
                  width={44}
                />
                {showLevel && (
                  <YAxis
                    yAxisId="level"
                    orientation="right"
                    domain={[1, 10]}
                    tick={{ fill: LEVEL_COLOR, fontSize: 11 }}
                    tickLine={false}
                    axisLine={false}
                    width={28}
                  />
                )}

                <Tooltip content={<ProjectionTooltip theme={theme} showLevel={showLevel} />} />

                {/* Confidence band around combined (stacked areas: invisible base + band) */}
                <Area
                  yAxisId="score"
                  dataKey="low"
                  stackId="band"
                  stroke="none"
                  fill="transparent"
                  isAnimationActive={false}
                  activeDot={false}
                />
                <Area
                  yAxisId="score"
                  dataKey="bandRange"
                  stackId="band"
                  stroke="none"
                  fill="url(#sppBand)"
                  isAnimationActive={false}
                  activeDot={false}
                />

                {/* Goal line */}
                {goalScore && (
                  <ReferenceLine
                    yAxisId="score"
                    y={goalScore}
                    stroke={GOAL_COLOR}
                    strokeWidth={2}
                    strokeDasharray="8 4"
                    label={{
                      value: `Goal: ${goalScore}`,
                      position: 'insideTopLeft',
                      fill: GOAL_COLOR,
                      fontSize: 11,
                      fontWeight: 500
                    }}
                  />
                )}

                {/* Today marker */}
                {todayTs && (
                  <ReferenceLine
                    yAxisId="score"
                    x={todayTs}
                    stroke={theme.axisLabel}
                    strokeWidth={1}
                    strokeDasharray="4 4"
                    strokeOpacity={0.5}
                    label={{
                      value: 'You are here',
                      position: 'insideTopRight',
                      fill: theme.axisLabel,
                      fontSize: 10
                    }}
                  />
                )}

                {/* Expected level track (secondary axis) - hidden by default */}
                {showLevel && (
                  <Line
                    yAxisId="level"
                    type="stepAfter"
                    dataKey="level"
                    name="Est. level"
                    stroke={LEVEL_COLOR}
                    strokeWidth={1.5}
                    strokeDasharray="2 3"
                    dot={false}
                    isAnimationActive={false}
                  />
                )}

                {/* Section trajectories */}
                <Line
                  yAxisId="score"
                  type="monotone"
                  dataKey="math"
                  name="Math"
                  stroke={MATH_COLOR}
                  strokeWidth={2}
                  dot={false}
                  animationDuration={400}
                />
                <Line
                  yAxisId="score"
                  type="monotone"
                  dataKey="rw"
                  name="R&W"
                  stroke={RW_COLOR}
                  strokeWidth={2}
                  dot={false}
                  animationDuration={400}
                />

                {/* Combined trajectory (primary) */}
                <Line
                  yAxisId="score"
                  type="monotone"
                  dataKey="combined"
                  name="Total"
                  stroke={COMBINED_COLOR}
                  strokeWidth={3}
                  dot={false}
                  animationDuration={400}
                />

                {/* Milestone markers on the combined line */}
                {milestones.map((m) => (
                  <ReferenceDot
                    key={m.percent}
                    yAxisId="score"
                    x={new Date(m.date).getTime()}
                    y={m.targetCombined}
                    r={5}
                    fill={GOAL_COLOR}
                    stroke="#fff"
                    strokeWidth={1.5}
                  />
                ))}
              </ComposedChart>
            </ResponsiveContainer>
          </div>

          {/* Plain-language read of the projection + a short "how to read this" key */}
          <div className="px-4 pt-1 pb-2 border-t border-gray-100 dark:border-gray-700">
            {finalCombined != null && (
              <p className="text-xs text-gray-600 dark:text-gray-300">
                By {formatDate(testTs)} you are on pace for about{' '}
                <span className="font-semibold" style={{ color: COMBINED_COLOR }}>
                  {finalCombined}
                </span>
                {finalLow != null && finalHigh != null ? (
                  <span className="text-gray-500 dark:text-gray-400">
                    {' '}
                    (likely {finalLow} to {finalHigh})
                  </span>
                ) : null}
                .
              </p>
            )}
            <div className="mt-2 grid grid-cols-1 gap-x-4 gap-y-1 text-xs text-gray-500 dark:text-gray-400 sm:grid-cols-2">
              <p>
                <span className="font-medium text-amber-600 dark:text-amber-400">Goal line</span>:
                the score you are aiming for by test day.
              </p>
              <p>
                <span className="font-medium" style={{ color: COMBINED_COLOR }}>
                  Shaded band
                </span>
                : the likely range around your projection, wider further out.
              </p>
              <p>
                <span className="font-medium text-amber-600 dark:text-amber-400">
                  Milestone dots
                </span>
                : checkpoints on the way to your goal.
              </p>
              {showLevel && (
                <p>
                  <span className="font-medium" style={{ color: LEVEL_COLOR }}>
                    Level track
                  </span>
                  : your estimated mastery level (1 to 10) over time.
                </p>
              )}
            </div>
          </div>

          {/* Milestone legend */}
          {milestones.length > 0 && (
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-2 border-t border-gray-100 dark:border-gray-700 text-xs text-gray-500 dark:text-gray-400">
              <span className="flex items-center gap-1 font-medium">
                <Flag className="w-3 h-3 text-amber-500" />
                Milestones:
              </span>
              {milestones.map((m) => (
                <span key={m.percent}>
                  {m.label} (~{formatDate(new Date(m.date).getTime())})
                </span>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  )
}

StudyPlanProjectionChart.propTypes = {
  data: PropTypes.shape({
    goalScore: PropTypes.number,
    testDate: PropTypes.string,
    weeks: PropTypes.arrayOf(
      PropTypes.shape({
        date: PropTypes.string,
        math: PropTypes.number,
        rw: PropTypes.number,
        combined: PropTypes.number,
        low: PropTypes.number,
        high: PropTypes.number,
        level: PropTypes.number
      })
    ),
    milestones: PropTypes.arrayOf(
      PropTypes.shape({
        percent: PropTypes.number,
        label: PropTypes.string,
        targetCombined: PropTypes.number,
        date: PropTypes.string,
        reachedByTestDate: PropTypes.bool
      })
    )
  }),
  height: PropTypes.number,
  className: PropTypes.string
}

export default StudyPlanProjectionChart
